import type { EcoleActivite, NouvelleSelection } from "@shared/ecole.types";
import { useCallback, useEffect, useState } from "react";
import { fetchJson, HttpError, messageDeSecours } from "@/lib/http";

/**
 * Les offres reçues et les profils envoyés d'UNE école, en une lecture.
 *
 * Chaque geste écrit, puis relit : le serveur est seul juge du statut (deux
 * référents peuvent trancher la même offre), et l'écran ne montre « publiée »
 * qu'après son accord.
 */

type State =
	| { status: "loading" }
	| { status: "failed"; message: string }
	| { status: "ready"; activite: EcoleActivite };

/** Ce qu'un geste rend à l'écran : rien quand il a réussi, sinon la phrase à afficher. */
export type Echec = string | null;

/** Les refus métier du serveur, dits au référent. Les autres prennent le message de secours. */
const MOTIFS: Record<string, string> = {
	OFFER_ALREADY_DECIDED: "Cette offre a déjà été traitée. La liste est à jour.",
	OFFER_UNKNOWN: "Cette offre n'est plus disponible. La liste est à jour.",
	RATE_LIMITED: "Trop d'envois d'un coup. Réessayez dans dix minutes.",
	INVITATIONS_PLAFOND:
		"Vous avez écrit à 20 nouvelles adresses aujourd'hui. Réessayez demain, ou passez par une offre reçue.",
	SCHOOL_UNKNOWN: "Cette école n'est plus dans vos accès.",
};

/** Les champs que le serveur nomme (`champs` d'un 400), dans les mots de l'écran. */
const CHAMPS: Record<string, string> = {
	expiresOn: "la date limite",
	motif: "le motif du refus",
	title: "le titre de l'envoi",
	message: "le message",
	email: "l'email du contact",
	contactName: "le nom du contact",
	destinataire: "le destinataire",
	offerId: "l'offre",
	fullName: "le nom de l'étudiant",
	program: "la formation",
	headline: "la ligne de présentation",
	cvUrl: "le lien vers le CV",
};

/** « profil 4, le lien vers le CV », depuis `profiles.3.cvUrl`. */
export function nommerChamp(chemin: string): string {
	const profil = chemin.match(/^profiles\.(\d+)\.(\w+)$/);
	if (profil) return `profil ${Number(profil[1]) + 1}, ${CHAMPS[profil[2] ?? ""] ?? "un champ"}`;
	return CHAMPS[chemin] ?? "un champ";
}

function champsRefuses(body: unknown): string[] {
	const champs = (body as { champs?: unknown } | null)?.champs;
	return Array.isArray(champs) ? champs.filter((c): c is string => typeof c === "string") : [];
}

function motifDe(error: unknown): string {
	if (error instanceof HttpError && error.failure.kind === "http") {
		if (error.failure.code === "INVALID_BODY") {
			const champs = champsRefuses(error.failure.body).map(nommerChamp);
			return champs.length > 0
				? `Valeur refusée : ${champs.join(" ; ")}. Corrigez-la, puis réessayez.`
				: "Une valeur n'est pas acceptée. Vérifiez les champs, puis réessayez.";
		}
		const motif = MOTIFS[error.failure.code];
		if (motif) return motif;
	}
	return messageDeSecours(error);
}

export function useActivite(brandId: string, onSessionFinie: () => void) {
	const [state, setState] = useState<State>({ status: "loading" });
	const base = `/api/ecole/marque/${brandId}`;

	const load = useCallback(async () => {
		try {
			const activite = await fetchJson<EcoleActivite>(`${base}/activite`);
			setState({ status: "ready", activite });
		} catch (error) {
			if (error instanceof HttpError && error.status === 401) {
				onSessionFinie();
				return;
			}
			setState({ status: "failed", message: messageDeSecours(error) });
		}
	}, [base, onSessionFinie]);

	useEffect(() => {
		void load();
	}, [load]);

	/**
	 * Écrit, puis relit dans TOUS les cas : un 409 dit que quelqu'un d'autre a déjà
	 * tranché, et l'écran doit montrer ce qu'il a tranché.
	 */
	const agir = useCallback(
		async (path: string, method: "POST" | "PATCH", body?: unknown): Promise<Echec> => {
			try {
				await fetchJson(`${base}${path}`, { method, body });
				await load();
				return null;
			} catch (error) {
				if (error instanceof HttpError && error.status === 401) {
					onSessionFinie();
					return null;
				}
				if (error instanceof HttpError && (error.status === 409 || error.status === 404)) {
					await load();
				}
				return motifDe(error);
			}
		},
		[base, load, onSessionFinie],
	);

	return {
		state,
		reload: load,
		publier: (offerId: string, expiresOn: string) =>
			agir(`/offres/${offerId}/publier`, "POST", { expiresOn }),
		refuser: (offerId: string, motif: string) =>
			agir(`/offres/${offerId}/refuser`, "POST", { motif }),
		changerDate: (offerId: string, expiresOn: string) =>
			agir(`/offres/${offerId}/date-limite`, "PATCH", { expiresOn }),
		envoyer: (selection: NouvelleSelection) => agir("/selections", "POST", selection),
	};
}

/**
 * Un geste en cours et son refus, pour un bouton : le même partout où l'école
 * tranche, envoie ou change une date.
 */
export function useGeste() {
	const [enCours, setEnCours] = useState(false);
	const [echec, setEchec] = useState<Echec>(null);

	async function lancer(geste: () => Promise<Echec>): Promise<Echec> {
		setEnCours(true);
		setEchec(null);
		const resultat = await geste();
		setEnCours(false);
		setEchec(resultat);
		return resultat;
	}

	return { enCours, echec, lancer };
}
